import React, { useEffect, useState } from 'react';
import './App.css';
import HomePage, { AccountCenterPage, BookDetailPage, BookshelfPage, BookstorePage, OrdersPage, PaymentPage, ProfileSettingsPage, ReaderPage } from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import WorkbenchPage from './pages/WorkbenchPage';
import { identifyAnalyticsSession, trackPageView } from './utils/analytics';
import { setSeoMetadata } from './utils/seo';

type RouteView = 'home' | 'library' | 'book-detail' | 'reader' | 'auth-login' | 'auth-register' | 'workbench' | 'account' | 'orders' | 'bookshelf' | 'profile' | 'payment';
interface RouteState {
  view: RouteView;
  slug?: string | null;
  chapterNo?: number;
  planId?: string | null;
  billingCycle?: 'month' | 'year';
}

const getRouteFromHash = (): RouteState => {
  const hash = window.location.hash;
  if (hash.startsWith('#workbench')) return { view: 'workbench' };
  if (hash === '#bookstore') return { view: 'library' };
  if (hash.startsWith('#library/book/')) {
    return { view: 'book-detail', slug: decodeURIComponent(hash.replace('#library/book/', '')) };
  }
  if (hash.startsWith('#library/read/')) {
    const [, slug, chapterNo] = hash.match(/^#library\/read\/([^/]+)\/?(\d+)?/) || [];
    return { view: 'reader', slug: slug ? decodeURIComponent(slug) : null, chapterNo: Number(chapterNo || 1) };
  }
  if (hash === '#library') return { view: 'library' };
  if (hash === '#account') return { view: 'account' };
  if (hash === '#orders') return { view: 'orders' };
  if (hash === '#bookshelf') return { view: 'bookshelf' };
  if (hash === '#profile') return { view: 'profile' };
  if (hash.startsWith('#payment/')) {
    const [, planId, billingCycle] = hash.match(/^#payment\/([^/]+)\/?(month|year)?/) || [];
    return { view: 'payment', planId: planId || 'growth', billingCycle: billingCycle === 'year' ? 'year' : 'month' };
  }
  if (hash === '#login') return { view: 'auth-login' };
  if (hash === '#register') return { view: 'auth-register' };
  if (hash === '#home') return { view: 'home' };
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/library') return { view: 'library' };
  if (path.startsWith('/library/book/')) {
    return { view: 'book-detail', slug: decodeURIComponent(path.replace('/library/book/', '')) };
  }
  if (path.startsWith('/library/read/')) {
    const [, slug, chapterNo] = path.match(/^\/library\/read\/([^/]+)\/?(\d+)?/) || [];
    return { view: 'reader', slug: slug ? decodeURIComponent(slug) : null, chapterNo: Number(chapterNo || 1) };
  }
  return { view: 'home' };
};

function App() {
  const [route, setRoute] = useState<RouteState>(getRouteFromHash);

  useEffect(() => {
    if (window.location.hash === '#bookstore') {
      window.history.replaceState(null, '', '#library');
    }
    consumeContentStudioSSO();
    trackContentStudioTraffic('page_view');
    document.documentElement.lang = 'zh-CN';
    document.documentElement.setAttribute('data-theme', localStorage.getItem('lingbi-theme') || 'light');

    const onHashChange = () => {
      setRoute(getRouteFromHash());
      trackContentStudioTraffic('page_view');
    };
    const onClick = () => trackContentStudioTraffic('click');
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    window.addEventListener('lingbi-route-change', onHashChange);
    window.addEventListener('click', onClick, { passive: true });
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onHashChange);
      window.removeEventListener('lingbi-route-change', onHashChange);
      window.removeEventListener('click', onClick);
    };
  }, []);

  useEffect(() => {
    const publicSeo: Partial<Record<RouteView, { title: string; description: string; path: string }>> = {
      home: {
        title: '灵笔 AI 小说创作平台｜从灵感到长篇小说',
        description: '灵笔提供小说灵感、世界观、角色、大纲与章节正文的一站式 AI 创作工具，帮助作者高效完成长篇小说创作。',
        path: '/',
      },
      library: {
        title: '书阁｜热门原创小说在线阅读 - 灵笔',
        description: '在灵笔书阁发现热门原创小说、最新连载与完结作品，按男频、女频、题材和热度快速筛选。',
        path: '/library',
      },
    };
    const metadata = publicSeo[route.view];
    if (metadata) {
      setSeoMetadata({
        ...metadata,
        jsonLd: route.view === 'home' ? {
          '@context': 'https://schema.org', '@type': 'WebApplication', name: '灵笔',
          url: new URL('/', window.location.origin).toString(), applicationCategory: 'CreativeWorkApplication',
          operatingSystem: 'Web', description: metadata.description,
        } : {
          '@context': 'https://schema.org', '@type': 'CollectionPage', name: '灵笔书阁',
          url: new URL('/library', window.location.origin).toString(), description: metadata.description,
        },
      });
    } else if (!['book-detail', 'reader'].includes(route.view)) {
      setSeoMetadata({ title: '灵笔 - AI 小说创作平台', description: '灵笔用户功能页面',
        path: window.location.pathname, robots: 'noindex,nofollow' });
    }
  }, [route.view]);

  useEffect(() => {
    const hash = window.location.hash;
    const isHomeAnchor = ['#welfare', '#workflow', '#price', '#faq', '#try', '#start'].includes(hash);
    if (!isHomeAnchor) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    const page = hash || '#home';
    identifyAnalyticsSession(page);
    trackPageView(page, {
      view: route.view,
      slug: route.slug,
      chapterNo: route.chapterNo,
    });
  }, [route.view, route.slug, route.chapterNo]);

  const navigate = (nextRoute: Exclude<RouteView, 'book-detail' | 'reader'>) => {
    if (nextRoute === 'home' || nextRoute === 'library') {
      window.history.pushState(null, '', nextRoute === 'home' ? '/' : '/library');
      window.dispatchEvent(new Event('lingbi-route-change'));
      return;
    }
    const hashMap: Record<Exclude<RouteView, 'book-detail' | 'reader'>, string> = {
      home: '#home',
      library: '#library',
      'auth-login': '#login',
      'auth-register': '#register',
      workbench: '#workbench/inspiration',
      account: '#account',
      orders: '#orders',
      bookshelf: '#bookshelf',
      profile: '#profile',
      payment: '#payment/growth',
    };
    window.location.hash = hashMap[nextRoute];
    setRoute(getRouteFromHash());
  };

  if (route.view === 'workbench') {
    return <WorkbenchPage onHome={() => navigate('home')} />;
  }

  if (route.view === 'auth-login' || route.view === 'auth-register') {
    return (
      <AuthPage
        initialMode={route.view === 'auth-login' ? 'login' : 'register'}
        onHome={() => navigate('home')}
        onSuccess={() => navigate('home')}
      />
    );
  }

  const pageProps = {
    onHome: () => navigate('home'),
    onLibrary: () => navigate('library'),
    onLogin: () => navigate('auth-login'),
    onRegister: () => navigate('auth-register'),
    onWorkbench: () => navigate('workbench'),
    onAccount: () => navigate('account'),
    onOrders: () => navigate('orders'),
    onBookshelf: () => navigate('bookshelf'),
    onProfile: () => navigate('profile'),
  };

  if (route.view === 'library') {
    return <BookstorePage {...pageProps} />;
  }

  if (route.view === 'book-detail') {
    return <BookDetailPage {...pageProps} slug={route.slug} />;
  }

  if (route.view === 'account') {
    return <AccountCenterPage {...pageProps} />;
  }

  if (route.view === 'orders') {
    return <OrdersPage {...pageProps} />;
  }

  if (route.view === 'bookshelf') {
    return <BookshelfPage {...pageProps} />;
  }

  if (route.view === 'profile') {
    return <ProfileSettingsPage {...pageProps} />;
  }

  if (route.view === 'payment') {
    return <PaymentPage {...pageProps} planId={route.planId} billingCycle={route.billingCycle} />;
  }

  if (route.view === 'reader') {
    return <ReaderPage {...pageProps} slug={route.slug} chapterNo={route.chapterNo} />;
  }

  return (
    <HomePage {...pageProps} />
  );
}

function consumeContentStudioSSO() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('source') !== 'content-studio') return;
  const token = params.get('token') || params.get('ssoToken') || params.get('accessToken');
  if (!token) return;
  const accountId = params.get('contentAccountId') || '';
  localStorage.setItem('lingbi-token', token);
  if (!localStorage.getItem('lingbi-user')) {
    localStorage.setItem(
      'lingbi-user',
      JSON.stringify({ id: Number(accountId) || accountId, username: '灵笔用户', source: 'content-studio' }),
    );
  }
  fetch(`${novelApiBaseUrl}/users_app/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error('user profile failed'))))
    .then((user) => {
      localStorage.setItem('lingbi-user', JSON.stringify({ ...user, source: 'content-studio' }));
      window.dispatchEvent(new Event('lingbi-auth-change'));
    })
    .catch(() => undefined);
  window.dispatchEvent(new Event('lingbi-auth-change'));
  window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.hash || ''}`);
  window.dispatchEvent(new Event('lingbi-auth-change'));
}

const defaultNovelApiUrl = window.location.hostname === 'localhost' ? 'http://localhost:65181' : window.location.origin;
const defaultNovelApiVersion = window.location.hostname === 'localhost' ? '/api/v1' : '/api/web';
const novelApiBaseUrl = `${process.env.REACT_APP_API_URL || defaultNovelApiUrl}${process.env.REACT_APP_API_VERSION === undefined ? defaultNovelApiVersion : process.env.REACT_APP_API_VERSION}`;
const defaultContentStudioApiBase = 'https://content-studio-api.cestalt.com';
const oldContentStudioApiBase = 'http://172.104.181.99:9088';
const contentStudioApiOverride = process.env.REACT_APP_CONTENT_STUDIO_API_BASE;
let lastTrafficClickAt = 0;
let contentStudioConfigPromise: Promise<{ enabled: boolean; baseUrl: string; platformCode: string }> | null = null;

function contentStudioAccountId() {
  try {
    const raw = localStorage.getItem('lingbi-user');
    if (!raw) return undefined;
    const id = JSON.parse(raw)?.id;
    const value = Number(id);
    return Number.isFinite(value) && value > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function normalizeContentStudioApiBase(value?: string) {
  const baseUrl = (value || defaultContentStudioApiBase).trim().replace(/\/+$/, '');
  if (!baseUrl || baseUrl === oldContentStudioApiBase) return defaultContentStudioApiBase;
  return baseUrl;
}

function contentStudioTrafficUrl(baseUrl: string, platformCode: string) {
  const normalized = normalizeContentStudioApiBase(baseUrl);
  const prefix = normalized.endsWith('/api/v1') ? normalized : `${normalized}/api/v1`;
  return `${prefix}/platforms/${encodeURIComponent(platformCode)}/traffic`;
}

function normalizeContentStudioPlatformCode(value?: string) {
  const code = String(value || '').trim();
  if (code === 'novel') return 'ai_novel';
  return code || 'ai_novel';
}

function readContentStudioConfig() {
  if (!contentStudioConfigPromise) {
    contentStudioConfigPromise = fetch(`${novelApiBaseUrl}/content_studio/config`)
      .then((resp) => (resp.ok ? resp.json() : Promise.reject(new Error('content-studio config failed'))))
      .then((data) => ({
        enabled: data?.enabled !== false,
        baseUrl: normalizeContentStudioApiBase(data?.base_url || contentStudioApiOverride),
        platformCode: normalizeContentStudioPlatformCode(data?.platform_code),
      }))
      .catch(() => ({
        enabled: true,
        baseUrl: normalizeContentStudioApiBase(contentStudioApiOverride),
        platformCode: normalizeContentStudioPlatformCode(),
      }));
  }
  return contentStudioConfigPromise;
}

function trackContentStudioTraffic(event: 'page_view' | 'click') {
  if (event === 'click') {
    const now = Date.now();
    if (now - lastTrafficClickAt < 1000) return;
    lastTrafficClickAt = now;
  }
  const target = `${window.location.pathname}${window.location.hash || ''}`;
  if (event === 'page_view') {
    const key = `content-studio-traffic:ai_novel:${target}`;
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
  }
  const payload = JSON.stringify({ accountId: contentStudioAccountId(), target, event });
  readContentStudioConfig()
    .then((config) => {
      if (!config.enabled) return;
      return fetch(contentStudioTrafficUrl(config.baseUrl, config.platformCode || 'ai_novel'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });
    })
    .catch(() => {});
}

export default App;
